import { backendApiClient } from '@geneo2-web/services-clients';
import { PAL_BASE_URL } from '@geneo2-web/shared-ui';
import axios, { AxiosRequestConfig } from 'axios';
import type { IConceptState, ILoMastery } from './module';
import {
  AnswerHintUsedEnum,
  AnswerModeEnum,
  Score,
  SubmissionDetails,
} from './common';

const CREATE_MODULE_SESSION_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/sessions/start`;

export async function createModuleSessionApi(
  body: {
    module_id: string;
  },
  options: AxiosRequestConfig = {}
): Promise<{ id?: string }> {
  return await backendApiClient.post<{ id?: string }>(
    CREATE_MODULE_SESSION_PATH,
    body,
    options
  );
}

const MODULE_SESSION_HISTORY_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/sessions/history`;

type ModuleSessionHistoryResponse = {
  sessions: { id: string; status: string }[];
};

export async function getModuleSessionHistoryApi(
  body: {
    module_id: string;
    status?: 'in_progress' | 'abandoned' | 'compeleted';
  },
  options: AxiosRequestConfig = {}
): Promise<ModuleSessionHistoryResponse> {
  return await backendApiClient.get<ModuleSessionHistoryResponse>(
    MODULE_SESSION_HISTORY_PATH,
    body,
    options
  );
}

const MODULE_SESSION_ABANDON_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/sessions/abandon`;

export async function abandonModuleSessionApi(
  body: {
    session_id: string;
  },
  options: AxiosRequestConfig = {}
): Promise<{ id?: string }> {
  return await backendApiClient.post<{ id?: string }>(
    MODULE_SESSION_ABANDON_PATH,
    body,
    options
  );
}

const MODULE_SESSION_END_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/sessions/end`;

export async function endModuleSessionApi(
  body: {
    session_id: string;
  },
  options: AxiosRequestConfig = {}
): Promise<{ id?: string }> {
  return await backendApiClient.post<{ id?: string }>(
    MODULE_SESSION_END_PATH,
    body,
    options
  );
}

const SESSION_RECOMMENDATIONs_NEXT_ACTION_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/sessions/recommendations/next-action`;

export type PalRecommendedAction =
  | 'answer_question'
  | 'view_content'
  | 'start_challenge'
  | 'answer_challenge_question';
export type PalNavigationDection =
  | 'stay'
  | 'promote'
  | 'remediate'
  | 'return_from_remediation'
  | 'not_applicable'
  | 'challenge'
  | 'module_complete';

export interface SessionRecommendationNextActionResponse {
  session_id?: string;
  concept_id?: string;
  concept_name?: string;
  recommended_action?: PalRecommendedAction;
  is_first_action?: boolean;
  is_diagnostic_seeded?: boolean;
  theta?: number;
  total_attempts?: number;
  eligibility_check?: {
    theta?: number;
    theta_content_threshold?: number;
    theta_gate_triggered?: boolean;
    struggling_lo_count?: number;
    n_struggling_lo_threshold?: number;
    lo_gate_triggered?: boolean;
    questions_since_last_content?: number;
    min_questions_before_content?: number;
    frequency_ok?: boolean;
    content_never_shown?: boolean;
    gate_policy?: string;
    gates_satisfied?: boolean;
    eligible_for_content?: boolean;
    explanation?: string;
  };
  recommend_diagnostic?: boolean;
  module_completion?: {
    module_completed?: boolean;
    total_concepts?: number;
    concepts_ready?: number;
  };
  navigation?: {
    decision?: PalNavigationDection;
    reason?: string;
    has_next_concept?: boolean;
    next_concept_id?: string;
    next_concept_name?: string;
    next_concept_emoji?: string;
    next_concept_title?: string;
    next_concept_module_id?: string;
    next_concept_module_name?: string;
    is_remediating?: boolean;
    is_cross_module_remediation?: boolean;
    remediation_depth?: number;
    coverage?: number;
    recent_accuracy?: number;
    practice_count?: number;
    struggle_count?: number;
  };
  remediation_stack?: IRemediationStack;
}

export async function getSessionRecommendationNextAction(
  body: {
    session_id: string;
  },
  options: AxiosRequestConfig = {}
): Promise<SessionRecommendationNextActionResponse> {
  return await backendApiClient.get<SessionRecommendationNextActionResponse>(
    SESSION_RECOMMENDATIONs_NEXT_ACTION_PATH,
    body,
    options
  );
}

const RECOMMENDATIONS_QUESTIONS_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/sessions/recommendations/questions`;

export interface RecommendationsQuestionsResponse {
  data: {
    concept_id: string;
    concept_name: string;
    recommendations: {
      question_id: string;
    }[];
  };
  question_mapping: Record<string, string>;
}

export async function getRecommendationsQuestionsAPI(
  body: {
    session_id: string;
    count?: number;
  },
  options: AxiosRequestConfig = {}
): Promise<RecommendationsQuestionsResponse> {
  return await backendApiClient.get<RecommendationsQuestionsResponse>(
    RECOMMENDATIONS_QUESTIONS_PATH,
    body,
    options
  );
}

const SESSION_NAVIGATE_PATH = (session_id: string) =>
  `${PAL_BASE_URL || ''}/adaptive/learners/sessions/${session_id}/navigate`;

export interface ISessionNavigateRequest {
  concept_id: string;
  decision?: PalNavigationDection;
  is_cross_module?: boolean;
}

export interface IRemediationStackEntry {
  module_id: string;
  concept_id: string;
  concept_name: string;
  reason: string;
  pushed_at: string;
}

export interface IRemediationStack {
  stack: IRemediationStackEntry[];
  depth: number;
  is_remediating: boolean;
  home_module_id: string;
}

export interface ISessionNavigateResponse {
  created_at: string;
  updated_at: string;
  id: string;
  learner_id: string;
  module_id: string;
  status: string;
  current_concept_id: string;
  current_concept_name: string;
  current_concept_module_id: string;
  started_at: string;
  ended_at?: string | null;
  summary_data?: Record<string, unknown>;
  event_count: number;
  questions_answered: number;
  correct_count: number;
  remediation_stack: IRemediationStack;
}

export async function sessionNavigateApi(
  session_id: string,
  body: ISessionNavigateRequest,
  options: AxiosRequestConfig = {}
): Promise<ISessionNavigateResponse> {
  return await backendApiClient.post<ISessionNavigateResponse>(
    SESSION_NAVIGATE_PATH(session_id),
    body,
    options
  );
}

const SESSION_RETURN_FROM_REMEDIATION_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/learners/sessions/${session_id}/return-from-remediation`;

export async function returnFromRemediationApi(
  session_id: string,
  options: AxiosRequestConfig = {}
): Promise<ISessionNavigateResponse> {
  return await backendApiClient.post<ISessionNavigateResponse>(
    SESSION_RETURN_FROM_REMEDIATION_PATH(session_id),
    {},
    options
  );
}

const GET_CONCEPT_MASTERY_PATH = (concept_id: string) =>
  `${PAL_BASE_URL || ''}/adaptive/learners/mastery/concepts/${concept_id}`;

export interface IConceptMasteryResponse {
  learner_id: string;
  concept_id: string;
  concept_name: string;
  concept_title: string;
  module_id: string;
  concept_state: IConceptState;
  lo_mastery: ILoMastery[];
}

export async function getConceptMasteryApi(
  params: {
    concept_id: string;
    session_id?: string;
  },
  options: AxiosRequestConfig = {}
): Promise<IConceptMasteryResponse> {
  const { concept_id, ...query } = params;
  return await backendApiClient.get<IConceptMasteryResponse>(
    GET_CONCEPT_MASTERY_PATH(concept_id),
    query,
    options
  );
}

const SUBMIT_ANSWER_PATH = (sessionId: string) =>
  `${PAL_BASE_URL || ''}/adaptive/learners/sessions/${sessionId}/answers`;

export interface ISubmitAnswerRequest {
  question_id: string;
  answer: string[];
  time_taken_seconds: number;
  answer_mode: AnswerModeEnum;
  hint_used?: AnswerHintUsedEnum;
  primary_concept_id?: string;
}

export interface IEntityUpdate {
  entity_type: string;
  entity_id: string;
  entity_name: string;
  before: number;
  after: number;
  delta: number;
}

export interface IConceptUpiScoreUpdate {
  concept_id: string;
  concept_name: string;
  before: number;
  after: number;
  delta: number;
}

export interface IPropagationDebug {
  target_concept_id: string;
  difficulty: number;
  score: number;
  ability: number;
  expected_prob: number;
  surprise: number;
  lr_multiplier: number;
  backward_set: Record<string, unknown>[];
  mastery_changes: Record<string, unknown>[];
  readiness_changes: Record<string, unknown>[];
  explanation: string;
}

export interface ISubmitAnswerResponse extends SubmissionDetails {
  event_id: string;
  session_id: string;
  lo_updates: IEntityUpdate[];
  concept_updates: IEntityUpdate[];
  navigation_decision: string;
  navigation_reason: string;
  next_concept_id: string;
  next_concept_name: string;
  concept_upi_score_updates: IConceptUpiScoreUpdate[];
  propagation_debug: IPropagationDebug;
  content_eligible: boolean;
}

export async function submitAnswerApi(
  sessionId: string,
  body: ISubmitAnswerRequest,
  options: AxiosRequestConfig = {}
): Promise<ISubmitAnswerResponse> {
  return await backendApiClient.post<ISubmitAnswerResponse>(
    SUBMIT_ANSWER_PATH(sessionId),
    body,
    options
  );
}

const FETCH_INTERNAL_QUESTION_ID_PATH = (questionId: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/learners/question/fetch-internal-id/${questionId}`;

export async function fetchInternalQuestionIdApi(
  questionId: string,
  options: AxiosRequestConfig = {}
): Promise<{ external_question_id: string; question_id: string }> {
  return await backendApiClient.get<{
    external_question_id: string;
    question_id: string;
  }>(FETCH_INTERNAL_QUESTION_ID_PATH(questionId), options);
}

const LATEST_RECOMMENDED_QUESTION_URL = (learnerId: string) =>
  `https://pal-prod.geneo.in/api/v1/pal/sessions/learners/${learnerId}/recommendations/questions/latest`;

export interface LatestRecommendedQuestionResponse {
  question_id: string;
}

export async function getLatestRecommendedQuestionApi(
  learnerId: string,
  options: AxiosRequestConfig = {}
): Promise<LatestRecommendedQuestionResponse> {
  const { data } = await axios.get<LatestRecommendedQuestionResponse>(
    LATEST_RECOMMENDED_QUESTION_URL(learnerId),
    options
  );
  return data;
}

const FETCH_METADATA_QUESTION_PATH = (questionId: string) =>
  `${PAL_BASE_URL || ''}/adaptive/learners/metadata/question/${questionId}`;

export interface IQuestionMetadataIdentifiedConcept {
  concept_id: string;
  concept_name: string;
  concept_external_id: string;
  coverage_score: number;
  relevance_score: number;
  rank: number;
}

export interface IQuestionMetadataIdentifiedLearningObjective {
  learning_objective_id: string;
  description: string;
  bloom_level: string;
  coverage_score: number;
}

export interface IQuestionMetadataSolutionStep {
  step_number: number;
  description: string;
  explanation: string;
}

export interface IQuestionMetadataHint {
  type: 'vague' | 'strong';
  text: string;
  order: number;
}

export interface IQuestionMetadataContext {
  prerequisite_knowledge: string[];
  common_misconceptions: string[];
  hints: IQuestionMetadataHint[];
  related_topics: string[];
  ideal_time_seconds: number;
}

export interface IQuestionMetadataResponse {
  created_at: string;
  updated_at: string;
  id: string;
  question_id: string;
  extraction_status: string;
  extraction_error: string;
  extracted_at: string;
  identified_concepts: IQuestionMetadataIdentifiedConcept[];
  identified_learning_objectives: IQuestionMetadataIdentifiedLearningObjective[];
  difficulty: string;
  difficulty_reasoning: string;
  solution: IQuestionMetadataSolutionStep[];
  context: IQuestionMetadataContext;
}

export async function fetchQuestionMetadataApi(
  questionId: string,
  options: AxiosRequestConfig = {}
): Promise<IQuestionMetadataResponse> {
  return await backendApiClient.get<IQuestionMetadataResponse>(
    FETCH_METADATA_QUESTION_PATH(questionId),
    options
  );
}

const FETCH_QUESTIONS_METADATA_BATCH_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/metadata/questions/fetch/batch`;

export interface IQuestionMetadataBatchResponse {
  questions: IQuestionMetadataResponse[];
  not_found: string[];
}

export async function fetchQuestionsMetadataBatchApi(
  body: {
    question_ids: string[];
  },
  options: AxiosRequestConfig = {}
): Promise<IQuestionMetadataBatchResponse> {
  return await backendApiClient.post<IQuestionMetadataBatchResponse>(
    FETCH_QUESTIONS_METADATA_BATCH_PATH,
    body,
    options
  );
}

export interface IRecommendationsContentItem {
  content_id: string;
  coverage_score: number;
  target_lo_ids: string[];
  concept_ids: string[];
  rank: number;
  selection_reason: string;
}

export interface RecommendationsContentItemsResponse {
  session_id: string;
  concept_id: string;
  concept_name: string;
  eligible_for_content: boolean;
  eligibility_reason: string;
  recommendations: IRecommendationsContentItem[];
  target_lo_id: string;
  target_lo_outcome: string;
  total_candidates: number;
  content_excluded: number;
  content_already_shown_count: number;
}

const RECOMMENDATIONS_CONTENT_ITEMS_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/learners/sessions/${session_id}/recommendations/content-items`;

export async function getRecommendationsContentItemsApi(
  session_id: string,
  body: {
    count?: number;
  },
  options: AxiosRequestConfig = {}
): Promise<RecommendationsContentItemsResponse> {
  return await backendApiClient.get<RecommendationsContentItemsResponse>(
    RECOMMENDATIONS_CONTENT_ITEMS_PATH(session_id),
    body,
    options
  );
}

const FETCH_INTERNAL_RESOURCE_ID_PATH = (contentId: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/learners/resource/fetch-internal-id/${contentId}`;

export async function fetchInternalResourceIdApi(
  contentId: string,
  options: AxiosRequestConfig = {}
): Promise<{ external_content_id: string; resource_id: string }> {
  return await backendApiClient.get<{
    external_content_id: string;
    resource_id: string;
  }>(FETCH_INTERNAL_RESOURCE_ID_PATH(contentId), options);
}

const CONTENT_VIEWED_SUBMIT_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/learners/sessions/${session_id}/content-viewed`;

interface IContentViewedResponse {
  event_id: string;
  session_id: string;
  content_id: string;
  concept_id: string;
  questions_until_next_content: number;
  content_shown_count: number;
}

export async function contentViewSubmitApi(
  session_id: string,
  body: {
    concept_id: string;
    content_id: string;
    time_spent_seconds?: number;
  },
  options: AxiosRequestConfig = {}
): Promise<IContentViewedResponse> {
  return await backendApiClient.post<IContentViewedResponse>(
    CONTENT_VIEWED_SUBMIT_PATH(session_id),
    body,
    options
  );
}

const MY_PERFORMANCE_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/my-performance`;

export interface IBreakdownEntry {
  correct: number;
  incorrect: number;
  unattempted: number;
  total: number;
  total_time_taken: number;
}

export interface IConceptSummary {
  concept_id: string;
  total_correct_questions: number;
  total_incorrect_questions: number;
  total_unattempted_questions: number;
  total_questions: number;
  title: string;
  name: string;
  emoji: string;
}

export interface IPerformanceQuestionData {
  question_id: string;
  concept_id: string;
  metadata: {
    bloom_level?: string;
    difficulty?: string;
    solution?: IQuestionMetadataSolutionStep[] | null;
  };
  eventReqBody?: {
    correct_answer?: Score['correct_answer'];
    score?: Pick<Score, 'rubric_type' | 'value'>;
    answer?: string[];
    answer_mode?: string;
    upi_period_key?: string;
    session_accuracy?: number;
    geneo_question_id?: string;
    time_taken_seconds?: number;
  };
}

export interface IPerformanceSummary {
  total_questions: number;
  total_correct_questions: number;
  total_incorrect_questions: number;
  total_unattempted_questions: number;
  total_time_taken: number;
  session_accuracy: number;
  difficulty_level_breakdown: Record<string, IBreakdownEntry>;
  bloom_level_breakdown: Record<string, IBreakdownEntry>;
  concepts: IConceptSummary[];
  questions_data: IPerformanceQuestionData[];
}

export interface IMyPerformanceResponse {
  results: IPerformanceSummary;
}

export async function getMyPerformanceApi(
  params?: { module_id?: string; session_id?: string },
  options: AxiosRequestConfig = {}
): Promise<IMyPerformanceResponse> {
  return await backendApiClient.get<IMyPerformanceResponse>(
    MY_PERFORMANCE_PATH,
    { ...options, params }
  );
}
